import trapFocus from './trap-focus';

/**
 * Product Filters Carousel
 * @param {object} opts
 */
function productFramingOptionsDropdown(opts) {
  const config = {
    dom: {
      atcBtn: 'pv-add__btn',
      labelButton: 'dropdown-list__item-btn',
      labelText: 'js-options-trigger-text',
      labelIcon: 'js-options-trigger-icon',
      dropdownHeading: 'dropdown-heading',
      dropdownItem: 'js-dropdown-list-item',
      dropdownMenu: 'js-options-dropdown-menu',
      dropdownWrapper: 'dropdownWrapper',
      optionsTrigger: 'js-options-trigger',
      optionsLabelIcon: 'options-label__icon',
      optionsWrapper: 'js-options-dropdown-wrapper',
    },
    cls: {
      active: 'is-active',
      fullHeight: 'full-height',
      hide: 'hide',
      hover: 'is-hovered',
      open: 'is-open',
      optionSelected: 'option-selected',
    },
  };
  const c = __.extend(config, opts);

  // global elements
  const $optionsWrapper = document.body.querySelectorAll(`.${c.dom.optionsWrapper}`);
  const $dropdownWrapper = document.getElementById(c.dom.dropdownWrapper);
  const $selectCustomOpts = document.querySelectorAll(`.${c.dom.dropdownItem}`);
  const $section = $dropdownWrapper?.closest('section').parentNode;
  const $labelTextElement = $section?.querySelector(`.${c.dom.labelText}`);
  const $labelIconElement = $section?.querySelector(`.${c.dom.labelIcon}`);
  const $labelIconImageElement = $section?.querySelector(`.${c.dom.labelIcon} image`);
  const $optionsTrigger = $section?.querySelector(`.${c.dom.optionsTrigger}`);
  const $atcBtn = document.querySelector(`.${c.dom.atcBtn}`);

  const optionsCount = $selectCustomOpts?.length;
  let optionHoveredIndex = -1;
  let view;

  /**
   * Set initial link state for CTA button
   * If no option is selected (default state), link to what-are-you-framing page
   * The base framing flow url is used in option links so we don't want to change that for all, only in this case
   * Forward the product and variant params from the original cta data-url so the
   * what-are-you-framing page can re-attach them to the framing-flow tile links.
   */
  const initDefaultLinkState = () => {
    if (!$atcBtn || !$optionsTrigger) {
      return;
    }

    const hasOptionSelected = __.hasClass($optionsTrigger, c.cls.optionSelected);
    if (hasOptionSelected) {
      return;
    }

    const defaultUrl = new URL("/pages/what-are-you-framing", window.location.origin);
    const { url } = $atcBtn.dataset || {};

    if (url) {
      try {
        const sourceUrl = new URL(url);
        const productId = sourceUrl.searchParams.get("product");
        const variantId = sourceUrl.searchParams.get("variant");

        if (productId) {
          defaultUrl.searchParams.set("product", productId);
        }
        if (variantId) {
          defaultUrl.searchParams.set("variant", variantId);
        }
      } catch (err) {
        // If data-url can't be parsed, fall back to the bare path.
      }
    }

    $atcBtn.href = `${defaultUrl.pathname}${defaultUrl.search}`;
  };

  function init() {
    if (!$optionsWrapper || !$optionsTrigger) return;

    addEvents();
    responsive();
    __.windowResize(responsive);
    initDefaultLinkState();
  }

  function addEvents() {
    __.addEvent({
      id: document.body,
      event: 'click',
      className: c.dom.optionsTrigger,
      fn: toggleDropdown,
    });

    __.addEvent({
      id: document.body,
      event: 'click',
      className: c.dom.dropdownItem,
      fn: (e) => {
        const { currentTarget } = e;
        handleItemClick(currentTarget);
      },
    });
  }

  /**
   * Responsive functions
   */
  function responsive() {
    __.mq({
      view: 'desktop',
      callback: () => {
        if (view !== 'desktop') {
          view = 'desktop';
        }
      },
    });

    __.mq({
      view: 'mobile',
      callback: () => {
        if (view !== 'mobile') {
          view = 'mobile';
        }
      },
    });
  }

  /**
   * Set keyboard accessibility for options
   * @param {object} e
   */
  function supportKeyboardNavigation(e) {
    // press down -> go next
    if (e.keyCode === 40 && optionHoveredIndex < optionsCount - 1) {
      e.preventDefault(); // prevent page scrolling
      updateCustomSelectHovered(optionHoveredIndex + 1);
    }

    // press up -> go previous
    if (e.keyCode === 38 && optionHoveredIndex > 0) {
      e.preventDefault(); // prevent page scrolling
      updateCustomSelectHovered(optionHoveredIndex - 1);
    }
    // press Enter or space -> select the option
    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault();
      const option = $selectCustomOpts[optionHoveredIndex];
      if (option) {
        __.removeClass(option, c.cls.hover);
        handleItemClick(option);
      }
    }
  }

  /**
   * Set hover state on options
   * @param {number} newIndex
   */
  function updateCustomSelectHovered(newIndex) {
    const $prevOption = $selectCustomOpts[optionHoveredIndex];
    const $option = $selectCustomOpts[newIndex];

    if ($prevOption) {
      __.removeClass($prevOption, c.cls.hover);
    }
    if ($option) {
      __.addClass($option, c.cls.hover);
    }

    optionHoveredIndex = newIndex;
  }

  /**
   * Set dropdown text to selected option
   * @param {HTMLElement} currentTarget
   */
  function handleItemClick(currentTarget) {
    const $listItems = document.body.querySelectorAll(`.${c.dom.dropdownItem}`);
    const { text } = currentTarget.dataset;
    const { icon } = currentTarget.dataset;
    const { slug } = currentTarget.dataset;

    [...$listItems].forEach((item) => {
      __.removeClass(item, c.cls.active);
    });

    __.addClass(currentTarget, c.cls.active);
    setTimeout(() => {
      toggleDropdown(currentTarget);
    }, 150);

    $labelTextElement.innerText = text;
    $labelIconImageElement.setAttribute('href', icon);
    __.addClass($optionsTrigger, c.cls.optionSelected);
    __.removeClass($labelIconElement, c.cls.hide);

    document.dispatchEvent(new CustomEvent('framingOption:selected', { detail: { slug } }));
  }

  /**
   * Update options and option state on toggle
   * @param {HTMLElement} currentTarget
   */
  function toggleDropdown(currentTarget) {
    const { url } = $atcBtn.dataset || null;
    $dropdownWrapper.classList.toggle(c.cls.active);
    const target = currentTarget.target || currentTarget;
    const { option, path, slug } = target.dataset || {};

    const atcHref = url
      ? new URL(url)
      : null;

    if (atcHref) {
      const { searchParams } = atcHref;

      if (option) {
        // We should use at= format
        searchParams.delete('art_type');  // Remove old format if present, was previously a default
        searchParams.set('at', slug || option);  // Use slug which should always be available, otherwise use option

        if (path) {
          // if path append to pathname
          atcHref.pathname = `${atcHref.pathname}${path}`;
        }

        $atcBtn.href = atcHref;
      }
    }

    const $optionsLabelParents = __.parents(target);
    const $dropdownMenu = $optionsLabelParents
      ? $optionsLabelParents.find(($parent) => $parent.classList.contains(c.dom.dropdownMenu))
      : null;
    const $dropdownHeading = $dropdownMenu
      ? $dropdownMenu.querySelector(`.${c.dom.dropdownHeading}`)
      : null;
    const $optionsLabelTrigger = $dropdownHeading
      ? $dropdownHeading.querySelector(`.${c.dom.optionsTrigger}`)
      : null;
    const $optionsLabelIcon = $optionsLabelTrigger
      ? $optionsLabelTrigger.querySelector(`.${c.dom.optionsLabelIcon}`)
      : null;

    if (view === 'desktop') {
      if (__.hasClass($dropdownWrapper, c.cls.active)) {
        $dropdownWrapper.focus();
        $optionsLabelTrigger.setAttribute('aria-expanded', 'true');
        $optionsLabelTrigger.setAttribute('aria-label', 'Close dropdown menu');
        __.addClass($optionsLabelIcon, c.cls.open);
        trapFocus($dropdownMenu);

        __.addEvent({
          id: $dropdownWrapper,
          event: 'keydown',
          fn: supportKeyboardNavigation,
        });
      } else {
        $dropdownWrapper.blur();
        $optionsLabelTrigger.focus();
        $optionsLabelTrigger.setAttribute('aria-expanded', 'false');
        $optionsLabelTrigger.setAttribute('aria-label', 'Open dropdown menu');
        __.removeClass($optionsLabelIcon, c.cls.open);

        __.removeEvent({
          id: $dropdownWrapper,
          event: 'keydown',
          fn: supportKeyboardNavigation,
        });
      }
    }

    resetOptions();
    const $button = target.querySelector(`.${c.dom.labelButton}`);
    $button?.setAttribute("aria-pressed", "true");
  }

  /**
   * Reset the state of all options
   */
  function resetOptions() {
    if (!$selectCustomOpts) return;

    $selectCustomOpts.forEach(($option) => {
      const $button = $option.querySelector(`.${c.dom.labelButton}`);
      $button?.setAttribute('aria-pressed', 'false');
    });
  }

  return init();
}

export default productFramingOptionsDropdown;
